import { ContentMediumType } from "../../constants/GlobalTypesAndEnumsAndConstants";

/** One tab-separated question/answer row the core importer accepts. */
function isImportableAnkiTextRow(line: string): boolean {
  const isEmptyLine = !line.trim();
  const isAnkiMetadataLine = line.startsWith("#");
  if (isEmptyLine || isAnkiMetadataLine) return false;
  const fields = line.split("\t");
  const [question, answer] = fields;
  if (!question?.trim() || !answer?.trim()) return false;

  // Match the core's placeholder classification without altering retained text.
  const isPlaceholderField = (field: string) => ["", "..."].includes(
    field.trim().replace(/^"+|"+$/g, "").replace(/<\/?(?:div|span|p|br|hr|b|i|u|strong|em)\b[^>]*>/gi, "").trim()
  );
  const hasImage = fields.slice(0, 4).some((field) => /<img\s+src=""[^"]+""/.test(field));
  return !isPlaceholderField(question) || !isPlaceholderField(answer) || hasImage;
}

/** Match the tab-separated question/answer rows supported by the core importer.
 * Anki headers are optional, and #html:false exports are valid plain text.
 */
export function hasImportableAnkiTextRows(text: string): boolean {
  return text.split("\n").some(isImportableAnkiTextRow);
}

/** Whether a .txt file is an Anki export ("Notes in Plain Text": Anki's own # headers, or nearly every line a
 * tab-separated card) rather than notes to generate questions from. */
export function looksLikeAnkiTextExport(text: string): boolean {
  const lines = text.split("\n").map((line) => line.replace(/\r$/, "")).filter((line) => line.trim());
  if (lines.slice(0, 12).some((line) => /^#(separator|html|notetype|deck|tags|guid)\b/i.test(line))) {
    return hasImportableAnkiTextRows(text);
  }
  const rows = lines.filter((line) => !line.startsWith("#"));
  return rows.length > 0 && rows.filter(isImportableAnkiTextRow).length >= rows.length * 0.8;
}

const TEXT_FILE_SNIFF_BYTES = 64 * 1024;

/** A .txt upload's medium from its first 64 KB: an Anki export is imported as cards, any other text is a
 * document (the backend lays it out as a PDF). An unreadable head keeps the Anki path the app always used. */
export async function getTextFileContentMediumType(file: Blob): Promise<ContentMediumType.AnkiTxt | ContentMediumType.PDF> {
  let head: string;
  try {
    // FileReader rather than Blob.text(): older Android WebViews lack the latter.
    head = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file.slice(0, TEXT_FILE_SNIFF_BYTES));
    });
  } catch {
    return ContentMediumType.AnkiTxt;
  }
  // The last line of a cut head may be partial.
  if (file.size > TEXT_FILE_SNIFF_BYTES) head = head.slice(0, Math.max(0, head.lastIndexOf("\n")));
  return looksLikeAnkiTextExport(head) ? ContentMediumType.AnkiTxt : ContentMediumType.PDF;
}
